import { Controller } from "@hotwired/stimulus"

// Handles inline save/approve and run-analysis on the Vision Review bulk page.
// Each card on the page is wrapped with data-controller="vision-card".
//
// Targets:
//   badge           — the status pill in the card header
//   analysisSection — the form + fields (shown when analysis exists)
//   emptySection    — the "no analysis yet" row (shown when no analysis)
//   flash           — small inline feedback message at the bottom of the card
//   form            — the vision fields form
//   structure, technique, backgroundColor, mainMotifColor,
//   accentColors, scale, motifs, compositionNotes — individual input/textarea fields
//
// Values:
//   updateUrl  — URL for update_vision_analysis (POST)
//   analyzeUrl — URL for run_vision_analysis (POST)

export default class extends Controller {
  static targets = [
    "badge", "analysisSection", "emptySection", "flash", "form",
    "structure", "technique", "backgroundColor", "mainMotifColor",
    "accentColors", "scale", "motifs", "compositionNotes"
  ]

  static values = {
    updateUrl:  String,
    analyzeUrl: String
  }

  // ── Save / Approve ───────────────────────────────────────────────────────
  // Attached to the form's submit event: data-action="submit->vision-card#save"
  async save(event) {
    event.preventDefault()

    const form      = event.target
    const submitter = event.submitter
    const approve   = submitter?.value === "1"

    const formData = new FormData(form)
    if (submitter?.name) formData.set(submitter.name, submitter.value)

    // Button feedback
    const origText = submitter?.textContent || ""
    if (submitter) { submitter.disabled = true; submitter.textContent = "Saving…" }

    try {
      const response = await fetch(this.updateUrlValue, {
        method:      "POST",
        body:        formData,
        headers:     { "Accept": "application/json" },
        credentials: "same-origin"
      })

      if (response.ok) {
        this.updateBadge(approve ? "approved" : "pending")
        this.showFlash(approve ? "Saved & approved ✓" : "Saved", approve ? "success" : "neutral")
      } else {
        this.showFlash("Save failed — please reload and try again", "error")
      }
    } catch (e) {
      this.showFlash(`Error: ${e.message}`, "error")
    } finally {
      if (submitter) { submitter.disabled = false; submitter.textContent = origText }
    }
  }

  // ── Run vision analysis ──────────────────────────────────────────────────
  // Attached to the "Run Analysis" button click: data-action="click->vision-card#runAnalysis"
  async runAnalysis(event) {
    event.preventDefault()

    if (!confirm("Run vision analysis? This will call the AI and takes a few seconds.")) return

    const button   = event.currentTarget
    const origText = button.textContent
    button.disabled   = true
    button.textContent = "Analyzing…"

    try {
      const response = await fetch(this.analyzeUrlValue, {
        method:      "POST",
        headers:     { "X-CSRF-Token": this.csrfToken(), "Accept": "application/json" },
        credentials: "same-origin"
      })

      if (response.ok) {
        const data = await response.json()
        this.populateFields(data.vision_analysis)
        this.showAnalysisSection()
        this.updateBadge("pending")
        this.showFlash("Analysis complete — review and approve", "neutral")
      } else {
        const data = await response.json().catch(() => ({}))
        this.showFlash(data.error || "Analysis failed — check OpenAI logs", "error")
        button.disabled   = false
        button.textContent = origText
      }
    } catch (e) {
      this.showFlash(`Error: ${e.message}`, "error")
      button.disabled   = false
      button.textContent = origText
    }
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  populateFields(va) {
    if (!va) return
    this.structureTarget.value        = va.structure        || ""
    this.techniqueTarget.value        = va.technique        || ""
    this.backgroundColorTarget.value  = va.background_color || ""
    this.mainMotifColorTarget.value   = va.main_motif_color || ""
    this.accentColorsTarget.value     = Array.isArray(va.accent_colors)
                                          ? va.accent_colors.join(", ")
                                          : (va.accent_colors || "")
    this.scaleTarget.value            = va.scale            || ""
    this.motifsTarget.value           = va.motifs           || ""
    this.compositionNotesTarget.value = va.composition_notes || ""
  }

  showAnalysisSection() {
    this.analysisSectionTarget.classList.remove("hidden")
    this.emptySectionTarget.classList.add("hidden")
  }

  updateBadge(state) {
    const badge = this.badgeTarget
    const el    = this.element

    // Reset border classes
    el.classList.remove("border-emerald-200", "border-amber-200", "border-stone-100")

    if (state === "approved") {
      badge.textContent = "✓ Approved"
      badge.className   = "text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full"
      el.classList.add("border-emerald-200")
    } else if (state === "pending") {
      badge.textContent = "Needs Review"
      badge.className   = "text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full"
      el.classList.add("border-amber-200")
    } else {
      badge.textContent = "No Analysis"
      badge.className   = "text-xs font-medium text-stone-400 bg-stone-50 px-2 py-0.5 rounded-full"
      el.classList.add("border-stone-100")
    }
  }

  showFlash(message, type = "neutral") {
    const flash  = this.flashTarget
    const colors = {
      success: "bg-emerald-50 text-emerald-700 border border-emerald-200",
      neutral: "bg-stone-50 text-stone-600 border border-stone-200",
      error:   "bg-red-50 text-red-700 border border-red-200"
    }
    flash.textContent = message
    flash.className   = `text-xs px-2 py-1.5 rounded mt-2 ${colors[type] || colors.neutral}`
    flash.classList.remove("hidden")
    clearTimeout(this._flashTimer)
    this._flashTimer = setTimeout(() => flash.classList.add("hidden"), 4000)
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
